import type { UniclientWizardComponentBase } from './types';

interface BaseFxProps<TEffect extends string> extends UniclientWizardComponentBase<'specialFx'> {
  readonly effect: TEffect;
  readonly trigger?: {
    readonly type: 'auto';
    readonly delay?: number;
  };
  readonly targetId?: string;
}

export interface ConfettiFxProps extends BaseFxProps<'confetti'> {
  readonly preset: 'sides' | 'top';
}

export interface TransformFxProps extends BaseFxProps<'transform'> {
  /** @optional css transform string */
  readonly transform: string;
  /** @optional css transform origin, defaults to `center left` */
  readonly origin?: string;
  /** @optional css duration + ease (e.g. `300ms ease-in`) */
  readonly transition?: string;
}

export interface AirFreshenerFxProps extends BaseFxProps<'airFreshener'> {
  readonly assets: {
    readonly front: string;
    readonly back: string;
    readonly width: number;
    readonly height: number;
  };
  readonly variant: 'primary' | 'secondary';
}

export interface Hover3dFxProps extends BaseFxProps<'hover3d'> {}

export interface RiveFxProps extends BaseFxProps<'rive'> {
  readonly src: string;
  readonly stateMachineName: string;
  readonly inputs: Record<string, string>;
  readonly texts?: Record<string, string>;
}

export type UniclientSpecialFxProps =
  | AirFreshenerFxProps
  | ConfettiFxProps
  | Hover3dFxProps
  | RiveFxProps
  | TransformFxProps;

/**
 * Use with caution, this effect is meant to be used sparingly.
 * DO NOT use it for UI heavy steps as it may have performance implications.
 *
 * The confetti effect will only target the current frame (title with component and action),
 * it will not affect the chat history, progress or summary.
 *
 * It may not work on images or other remote resources.
 */
export function confetti(props: Omit<ConfettiFxProps, 'effect' | 'type'>): ConfettiFxProps {
  return { ...props, effect: 'confetti', type: 'specialFx' };
}

/**
 * Use with caution, this effect is meant to be used sparingly.
 *
 * The transform effect will only target the current components,
 * it will not affect the chat title, actions, history, progress or summary.
 */
export function transform(props: Omit<TransformFxProps, 'effect' | 'type'>): TransformFxProps {
  return {
    trigger: {
      type: 'auto',
      delay: 0,
    },
    ...props,
    effect: 'transform',
    type: 'specialFx',
  };
}

export function airFreshener(props: Omit<AirFreshenerFxProps, 'effect' | 'type'>): AirFreshenerFxProps {
  return {
    ...props,
    effect: 'airFreshener',
    type: 'specialFx',
  };
}

export function hover3d(props: Omit<Hover3dFxProps, 'effect' | 'trigger' | 'type'> = {}): Hover3dFxProps {
  return {
    ...props,
    trigger: {
      type: 'auto',
      delay: 0,
    },
    effect: 'hover3d',
    type: 'specialFx',
  };
}

export const RIVE_SPATIAL_INPUT_PREFIX = 'RIVE_SPATIAL_INPUT_';
export const RIVE_SPATIAL_INPUT_X = `${RIVE_SPATIAL_INPUT_PREFIX}X`;
export const RIVE_SPATIAL_INPUT_Y = `${RIVE_SPATIAL_INPUT_PREFIX}Y`;
export const RIVE_SPATIAL_INPUT_Z = `${RIVE_SPATIAL_INPUT_PREFIX}Z`;

export function rive(props: Omit<RiveFxProps, 'effect' | 'type'>): RiveFxProps {
  return {
    trigger: {
      type: 'auto',
      delay: 0,
    },
    ...props,
    effect: 'rive',
    type: 'specialFx',
  };
}

export const specialFx = {
  confetti,
  transform,
  airFreshener,
  hover3d,
  rive: Object.assign(rive, {
    spatial: {
      x: RIVE_SPATIAL_INPUT_X,
      y: RIVE_SPATIAL_INPUT_Y,
      z: RIVE_SPATIAL_INPUT_Z,
    },
  }),
};
